import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { PublicOnlyRoute } from '@components/auth/PublicOnlyRoute'
import { MainLayout } from '@components/layout/MainLayout'
import { AuthLayout } from '@components/layout/AuthLayout'

// Pages
import TrailsPage from '@pages/TrailsPage'
import BadgesPage from '@pages/BadgesPage'
import LeaderboardPage from '@pages/LeaderboardPage'
import CollectionsPage from '@pages/CollectionsPage'
import LoginPage from '@features/auth/pages/LoginPage'
import RegisterPage from '@features/auth/pages/RegisterPage'
import MfaVerifyPage from '@features/auth/pages/MfaVerifyPage'
import ForgotPasswordPage from '@features/auth/pages/ForgotPasswordPage'
import VerifyResetCodePage from '@features/auth/pages/VerifyResetCodePage'
import ResetPasswordPage from '@features/auth/pages/ResetPasswordPage'
import { SearchResultsPage } from '@features/search/pages/SearchResultsPage'
import { AdvancedSearchPage } from '@features/search/pages/AdvancedSearchPage'
import { SocialHubPage } from '@features/social/pages/SocialHubPage'
import { GroupDetailsPage } from '@features/social/pages/GroupDetailsPage'
import { ShareCodePage } from '@features/social/pages/ShareCodePage'
import AdventureListPage from '@features/adventures/pages/AdventureListPage'
import AdventureDetailPage from '@features/adventures/pages/AdventureDetailPage'
import AdventureCreatePage from '@features/adventures/pages/AdventureCreatePage'
import AdventureEditPage from '@features/adventures/pages/AdventureEditPage'
import MyAdventuresPage from '@features/adventures/pages/MyAdventuresPage'
import QRScannerPage from '@features/adventures/pages/QRScannerPage'
import ProfilePage from '@features/profile/pages/ProfilePage'
import ProfileEditPage from '@features/profile/pages/ProfileEditPage'
import SettingsPage from '@features/profile/pages/SettingsPage'
import ChatPage from '@features/chat/pages/ChatPage'
import NotificationsPage from '@features/notifications/pages/NotificationsPage'
import SubscriptionPage from '@features/subscriptions/pages/SubscriptionPage'
import MyRewardsPage from '@features/rewards/pages/MyRewardsPage'
import RewardDetailPage from '@features/rewards/pages/RewardDetailPage'
import GroupRewardTemplatesPage from '@features/rewards/pages/GroupRewardTemplatesPage'
import GroupRewardTemplateEditPage from '@features/rewards/pages/GroupRewardTemplateEditPage'
import GroupRewardTemplateStatsPage from '@features/rewards/pages/GroupRewardTemplateStatsPage'
import MediaManagerPage from '@features/media/pages/MediaManagerPage'
import CreateTicketPage from '@features/tickets/pages/CreateTicketPage'
import MyTicketsPage from '@features/tickets/pages/MyTicketsPage'
import TicketDetailPage from '@features/tickets/pages/TicketDetailPage'
import ModeratorTicketsPage from '@features/tickets/pages/ModeratorTicketsPage'
import ModeratorTicketDetailPage from '@features/tickets/pages/ModeratorTicketDetailPage'
import CalendarEventListPage from '@features/calendarEvents/pages/CalendarEventListPage'
import CalendarEventCreatePage from '@features/calendarEvents/pages/CalendarEventCreatePage'
import MyEventsPage from '@features/calendarEvents/pages/MyEventsPage'
import CalendarEventDetailPage from '@features/calendarEvents/pages/CalendarEventDetailPage'
import CalendarEventEditPage from '@features/calendarEvents/pages/CalendarEventEditPage'
import CollectionEditorPage from '@features/adventures/pages/CollectionEditorPage'
import ModeratorApprovalsPage from '@features/adventures/pages/ModeratorApprovalsPage'
import UnauthorizedPage from '@pages/UnauthorizedPage'
import NotFoundPage from '@pages/NotFoundPage'

// Layout wrapper components to prevent remounting on navigation
function ProtectedMainLayout() {
  return (
    <ProtectedRoute>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </ProtectedRoute>
  )
}

function PublicAuthLayout() {
  return (
    <PublicOnlyRoute>
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    </PublicOnlyRoute>
  )
}

// Root redirect that handles /?{code} format
function RootRedirect() {
  const location = useLocation()

  // Check if there's a query string without a key (e.g., ?U12345 or ?ABC123)
  // location.search will be "?U12345" for /?U12345
  if (location.search && location.search.length > 1) {
    const code = location.search.substring(1) // Remove the leading ?
    // Only redirect if it looks like a code (no = sign, which would indicate a key-value pair)
    if (!code.includes('=')) {
      return <Navigate to={`/code/${encodeURIComponent(code)}`} replace />
    }
  }

  return <Navigate to="/adventures" replace />
}

const router = createBrowserRouter([
  // Root redirect - must be before protected routes to handle /?{code} for unauthenticated users
  {
    path: '/',
    element: <RootRedirect />,
  },
  // Share code routes - public so they can handle auth redirects with referral codes
  {
    path: '/code/:shareCode',
    element: <ShareCodePage />,
  },
  {
    path: '/code',
    element: <ShareCodePage />,
  },
  // Public auth routes (login, register, etc.)
  {
    element: <PublicAuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-reset-code', element: <VerifyResetCodePage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
    ],
  },
  // MFA verify (no PublicOnlyRoute - user might be partially authenticated)
  {
    path: '/mfa-verify',
    element: (
      <AuthLayout>
        <MfaVerifyPage />
      </AuthLayout>
    ),
  },
  // QR Scanner (fullscreen, no MainLayout)
  {
    path: '/adventures/:id/scan',
    element: (
      <ProtectedRoute>
        <QRScannerPage />
      </ProtectedRoute>
    ),
  },
  // Main protected routes with MainLayout
  {
    element: <ProtectedMainLayout />,
    children: [
      { path: '/trails', element: <TrailsPage /> },
      { path: '/badges', element: <BadgesPage /> },
      { path: '/leaderboard', element: <LeaderboardPage /> },
      { path: '/collections', element: <CollectionsPage /> },
      { path: '/collections/create', element: <CollectionEditorPage /> },
      { path: '/collections/:id', element: <CollectionEditorPage /> },
      { path: '/social', element: <SocialHubPage /> },
      { path: '/trail-buddies', element: <Navigate to="/social?tab=buddies" replace /> },
      { path: '/groups', element: <Navigate to="/social?tab=groups" replace /> },
      { path: '/groups/:id', element: <GroupDetailsPage /> },
      { path: '/groups/:id/rewards', element: <GroupRewardTemplatesPage /> },
      { path: '/groups/:id/rewards/new', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/edit', element: <GroupRewardTemplateEditPage /> },
      { path: '/groups/:id/rewards/:templateId/stats', element: <GroupRewardTemplateStatsPage /> },
      { path: '/media', element: <MediaManagerPage /> },
      { path: '/search', element: <SearchResultsPage /> },
      { path: '/search/advanced', element: <AdvancedSearchPage /> },
      { path: '/adventures', element: <AdventureListPage /> },
      { path: '/adventures/create', element: <AdventureCreatePage /> },
      { path: '/adventures/:id', element: <AdventureDetailPage /> },
      { path: '/adventures/:id/edit', element: <AdventureEditPage /> },
      { path: '/moderator-approvals', element: <ModeratorApprovalsPage /> },
      { path: '/my-adventures', element: <MyAdventuresPage /> },
      { path: '/my-adventures/:tab', element: <MyAdventuresPage /> },
      { path: '/events', element: <CalendarEventListPage /> },
      { path: '/events/create', element: <CalendarEventCreatePage /> },
      { path: '/events/my-events', element: <MyEventsPage /> },
      { path: '/events/:eventId', element: <CalendarEventDetailPage /> },
      { path: '/events/:eventId/edit', element: <CalendarEventEditPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/profile/:userId', element: <ProfilePage /> },
      { path: '/profile/edit', element: <ProfileEditPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/subscription', element: <SubscriptionPage /> },
      { path: '/rewards', element: <MyRewardsPage /> },
      { path: '/rewards/:instanceId', element: <RewardDetailPage /> },
      { path: '/chat', element: <ChatPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/support/new', element: <CreateTicketPage /> },
      { path: '/support/tickets', element: <MyTicketsPage /> },
      { path: '/support/tickets/:ticketId', element: <TicketDetailPage /> },
      { path: '/tickets', element: <ModeratorTicketsPage /> },
      { path: '/tickets/:ticketId', element: <ModeratorTicketDetailPage /> },
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
